import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown } from "@phosphor-icons/react";
import { MaskedLines } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

export const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.85, 1]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen min-h-[720px] overflow-hidden">
      <motion.div className="absolute inset-0 scale-110" style={{ y: imgY }}>
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A]/70 via-[#0D1B2A]/60 to-[#0D1B2A]"
        style={{ opacity: overlayOpacity }}
      />

      <motion.div style={{ y: textY }} className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="text-xs uppercase tracking-[0.35em] text-gold mb-8"
        >
          Social Media Marketing Agency
        </motion.p>

        <h1 className="font-heading font-light text-[#F6F4F0] text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
          <MaskedLines lines={["We build brands", "that people"]} />
          <span className="line-mask">
            <motion.span
              className="block italic text-gold"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              choose to follow.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
        >
          <p className="text-[#E5E7EB] text-base sm:text-lg font-light max-w-md leading-relaxed">
            Social-first strategy, content and campaigns engineered to grow your business — one post at a time.
          </p>
          <button
            data-testid="hero-consultation-btn"
            onClick={() => navigate("/consultation")}
            className="shrink-0 text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#C8A96A] text-[#0D1B2A] hover:bg-[#F6F4F0] transition-colors duration-300 font-medium"
          >
            Start a Conversation
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-gold"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
